<template>
  <QPage>
    <LoginForm
      :prefilled-email="prefilledEmail"
      @on-login="onLogin"
      @go-to-signup="goToSignup"
      @on-password-forget="onPasswordForget"
      @on-email-forget="onEmailForget"
    />
    <IconTextConfirmModal
      v-model="opened"
      :on-close-modal="closeModal"
      :on-confirm-modal="modalForPassword ? onConfirmPasswordForget : onConfirmEmailForget"
      icon="icon-extra-keychain"
      :confirm="modalForPassword ? $t('CORE.BUTTONS.@GREAT') : $t('CORE.BUTTONS.@LETS_GO')"
      :refuse="$t('CORE.BUTTONS.@NO_THANKS')"
      :text="modalForPassword ? $t('INFO.@FORGOT_PASSWORD_MESSAGE') : $t('INFO.@FORGOT_EMAIL_MESSAGE')"
      confirm-color="secondary"
      refuse-color="primary"
      @hide="closeModal"
    />
  </QPage>
</template>
<script>
  import { JavascriptHelpers, MetaHelpers } from "@core/helpers";
  import { NotificationsManager } from "@core/managers/notifications.manager";
  import { AuthenticationManager } from "@core/managers/authentication.manager";
  import { KochavaManager } from "@core/managers/kochava.manager";
  import { IconTextConfirmModal } from "@core/components/modals";
  import { ETAB_ROUTES } from "@etab/config";
  import { TYPEFORM_LOGIN_URL, ROLE } from "@core/constants";
  import { UserStorageManager } from "@core/managers/user-storage.manager";
  import { ContactManager } from "@core/managers/contact.manager";
  import { TrackingManager } from "@core/managers/tracking.manager";
  import { PosthogManager } from "@etab/managers/posthog.manager";
  import { getActivePinia } from "pinia";

  import LoginForm from "@etab/components/auth/LoginForm.component.vue";

  export default {
    components: {
      LoginForm,
      IconTextConfirmModal,
    },
    meta() {
      return MetaHelpers.setAutoMeta({
        description: this.$t("ETAB.PAGES.LOGIN.META.@DESCRIPTION"),
        canonical: `${window.location.origin}${window.location.pathname}${window.location.hash.split("?")[0]}`,
      });
    },
    data() {
      return {
        opened: false,
        modalForPassword: false,
      };
    },
    computed: {
      prefilledEmail() {
        return this.$route.query.prefilledEmail;
      },
    },
    mounted() {
      // safe guard in case of coming from another call or problem during startup
      this.$_stopGlobalLoading();
    },
    methods: {
      async onLogin({ login, password }) {
        try {
          this.$_startGlobalLoading();
          // we reset store before login in order to start with a clean state
          getActivePinia()._s.forEach((store) => store.$reset());
          // we ask the api for auth
          const loginSuccess = await AuthenticationManager.login({ login, password });
          if (loginSuccess) {
            this.postLogin();
            await PosthogManager.setUserIdentify();
            TrackingManager.addEvent({
              event: "login",
            });
            KochavaManager.sendEventLogin();
          } else {
            this.$_stopGlobalLoading();
            TrackingManager.addEvent({
              event: "login_error",
            });
            // if auth comes back in success and the user is still not authenticated, we have a problem somewhere
            // let's notify the user and let him try again
            NotificationsManager.error({ message: "ERRORS.@LOGIN_FAIL" });
          }
        } catch (error) {
          this.$_stopGlobalLoading();
          const status = error && error.request && error.request.status;
          switch (status) {
            case 0:
              TrackingManager.addEvent({
                event: "login_error",
              });
              NotificationsManager.error({ message: "ERRORS.HTTP.@COULDNT_REACH" });
              break;
            case 401:
              TrackingManager.addEvent({
                event: "login_error",
              });
              NotificationsManager.error({ message: "ERRORS.@WRONG_CREDENTIALS" });
              break;
            case 410:
              TrackingManager.addEvent({
                event: "login_error",
              });
              NotificationsManager.create({
                message: "ERRORS.@ACCOUNT_DELETED",
                options: {
                  color: "info",
                  closeBtn: false,
                  actions: [
                    {
                      icon: "icon-f-mail",
                      color: "white",
                      handler: () => {
                        ContactManager.contactExtracadabraDpo({ subject: this.$t("EMAILS.@REACTIVATE_ACCOUNT", { email: login }) });
                      },
                    },
                  ],
                },
              });
              break;
            default:
              TrackingManager.addEvent({
                event: "login_error",
              });
              // whatever happens outside of documented cases, we say oops
              NotificationsManager.error({ message: "ERRORS.@SOMETHING_WENT_WRONG" });
              throw error;
          }
        } finally {
          // this.$_stopGlobalLoading(); // stop loading in later stages
        }
      },
      goToSignup({ prefilledEmail }) {
        this.$router.push({ name: ETAB_ROUTES.auth.signup.name, query: { prefilledEmail } }).catch((suppressedError) => {});
      },
      postLogin() {
        this.masterEtabLogin();
        // get the optional redirect (if coming from a failed navigation)
        this.$router.replace(this.$route.query.redirect || { name: ETAB_ROUTES.smartRedirect.name }).catch((suppressedError) => {});
      },
      closeModal() {
        this.opened = false;
        this.modalForPassword = false;
      },
      async onConfirmPasswordForget() {
        try {
          this.$_startGlobalLoading();
          await AuthenticationManager.generateMagicLink({ email: this.email });
          NotificationsManager.success({ message: "SUCCESS.@FORGOT_PASSWORD_EMAIL_SENT" });
        } catch (error) {
          const status = error && error.request && error.request.status;
          switch (status) {
            case 403: // email does not exist
              NotificationsManager.error({ message: "ERRORS.@FORGOT_PASSWORD_EMAIL_NOT_EXIST" });
              break;
            case 400: // email does not exist
              NotificationsManager.error({ message: "ERRORS.@FORGOT_PASSWORD_EMAIL_NOT_EXIST" });
              break;
            default:
              NotificationsManager.error({ message: "ERRORS.@SOMETHING_WENT_WRONG" });
              break;
          }
          throw error;
        } finally {
          this.closeModal();
          this.$_stopGlobalLoading();
        }
      },
      onConfirmEmailForget() {
        JavascriptHelpers.openUrl(TYPEFORM_LOGIN_URL, "_blank");
        this.opened = false;
      },
      onPasswordForget(email) {
        this.modalForPassword = true;
        this.opened = true;
        this.email = email;
      },
      onEmailForget() {
        this.opened = true;
      },
      async masterEtabLogin() {
        try {
          const user = await UserStorageManager.getUser();
          const role = user.role[0];

          if (role === ROLE.MASTER) {
            this.$router.push({ name: ETAB_ROUTES.account.multiAccount.name, params: { role } }).catch((suppressedError) => {});
          }
        } catch (error) {
          throw error;
        }
      },
    },
  };
</script>
